import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAttachmentsPanelComponent } from '../../shared/design-system/attachments-panel.component';
import { AinAttachmentItem } from '../../shared/design-system/attachment.model';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { FinancialAccountSelectorComponent } from '../../shared/design-system/financial-account-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import {
  AccountingCustomerRecord,
  AccountingWarehouseRecord,
  BankRecord,
  CashboxRecord,
  CreateJournalEntryPayload,
  FinanceApiService,
  FinancialAccountRecord,
  FinancialBook,
  FiscalPeriodRecord,
  JournalEntryRecord,
  UpdateJournalEntryPayload
} from './finance-api.service';

type JournalTab = 'items' | 'attachments';
type FloatingEntityType = 'None' | 'Person' | 'OrganizationUnit' | 'Bank' | 'CashFund' | 'Warehouse';
type FixedJournalEntryType = 'opening' | 'closing';

interface DraftJournalLine {
  accountId: string;
  floatingEntityKey: string;
  description: string;
  reference: string;
  debit: number;
  credit: number;
}

interface FloatingEntityOption {
  key: string;
  label: string;
}

interface DraftAttachment {
  name: string;
  file: File;
}

@Component({
  standalone: true,
  selector: 'ain-finance-journal-entry',
  imports: [
    CommonModule,
    AinPageComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    AinDatePipe,
    JalaliDateInputComponent,
    CurrencySelectorComponent,
    FinancialAccountSelectorComponent,
    AinAttachmentsPanelComponent,
    DialogModule
  ],
  template: `
    <ain-page [title]="t(pageTitleKey())" [description]="t(pageDescriptionKey())">
      @if (mode() === 'list') {
        <section class="card">
          <div class="toolbar">
            <p-select
              class="field-select filter-book"
              [options]="searchBookOptions()"
              optionLabel="label"
              optionValue="id"
              [filter]="true"
              filterBy="label"
              [(ngModel)]="searchBookId"
              (ngModelChange)="loadJournals()"
              appendTo="body">
            </p-select>
            <button pButton type="button" [label]="t('finance.journal.refresh')" severity="secondary" (click)="loadJournals()"></button>
            <button pButton type="button" [label]="t('common.create')" (click)="openCreateRoute()"></button>
          </div>

          <p-table [value]="filteredJournals()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('finance.journal.fields.number') }}</th>
                <th>{{ t('finance.journal.fields.date') }}</th>
                <th>{{ t('finance.journal.fields.book') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('finance.journal.fields.description') }}</th>
                <th>{{ t('finance.journal.actions.title') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.number }}</td>
                <td>{{ row.postingDateUtc | ainDate }}</td>
                <td>{{ bookLabel(row.bookId) }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.description }}</td>
                <td class="action-buttons">
                  <button pButton type="button" [label]="t('common.edit')" size="small" severity="secondary" text="true" [disabled]="row.status !== 'Draft'" (click)="openEditRoute(row)"></button>
                  <button pButton type="button" [label]="t('common.delete')" size="small" severity="danger" text="true" [disabled]="row.status !== 'Draft'" (click)="deleteJournal(row)"></button>
                  <button pButton type="button" [label]="t('finance.journal.actions.submit')" size="small" severity="secondary" text="true" (click)="submitJournal(row.id)"></button>
                  <button pButton type="button" [label]="t('finance.journal.actions.approve')" size="small" severity="secondary" text="true" (click)="approveJournal(row.id)"></button>
                  <button pButton type="button" [label]="t('finance.journal.actions.post')" size="small" severity="secondary" text="true" (click)="postJournal(row.id)"></button>
                  <button pButton type="button" [label]="t('finance.journal.actions.reverse')" size="small" severity="danger" text="true" (click)="reverseJournal(row.id)"></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      } @else {
        <section class="card editor-header">
          <div class="header-top">
            <h3>{{ editJournalId() ? t('finance.journal.form.editTitle') : t('finance.journal.form.createTitle') }}</h3>
            <div class="actions">
              @if (canGeneratePeriodLines()) {
                <button
                  pButton
                  type="button"
                  [label]="generateLinesLabel()"
                  severity="secondary"
                  [loading]="generateLinesLoading()"
                  (click)="generatePeriodLines()"></button>
              }
              <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="backToList()"></button>
              <button pButton type="button" [label]="t('common.save')" (click)="saveJournal()"></button>
            </div>
          </div>
          <div class="form-grid">
            <label>
              <span>{{ t('finance.journal.fields.book') }}</span>
              <p-select
                class="field-select"
                [options]="books()"
                optionValue="id"
                [filter]="true"
                filterBy="code,name"
                [(ngModel)]="form.bookId"
                (ngModelChange)="onEditBookChanged()"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-book>
                  {{ book ? (book.code + ' - ' + book.name) : '-' }}
                </ng-template>
                <ng-template pTemplate="item" let-book>
                  {{ book.code }} - {{ book.name }}
                </ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('finance.journal.fields.number') }}</span>
              <input pInputText type="text" [ngModel]="form.number" (ngModelChange)="onNumberChanged($event)" />
            </label>
            <label>
              <span>{{ t('finance.journal.fields.date') }}</span>
              <ain-date-input [(ngModel)]="form.postingDateUtc" />
            </label>
            <label>
              <span>{{ t('finance.journal.fields.period') }}</span>
              <p-select
                class="field-select"
                [options]="periods()"
                optionValue="id"
                [filter]="true"
                filterBy="fiscalYear,periodNumber,status"
                [(ngModel)]="form.periodId"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-period>
                  {{ period ? periodLabel(period) : '-' }}
                </ng-template>
                <ng-template pTemplate="item" let-period>
                  {{ periodLabel(period) }}
                </ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('finance.journal.fields.currencyCode') }}</span>
              <ain-currency-selector
                [showSearch]="true"
                [ngModel]="form.currencyCode"
                (ngModelChange)="form.currencyCode = $event ?? ''" />
            </label>
            <label>
              <span>{{ t('finance.journal.fields.reference') }}</span>
              <input pInputText type="text" [(ngModel)]="form.reference" />
            </label>
            @if (!fixedEntryType()) {
              <label>
                <span>{{ t('finance.journal.fields.type') }}</span>
                <p-select
                  class="field-select"
                  [options]="journalTypeOptions"
                  optionValue="value"
                  [(ngModel)]="form.type"
                  [disabled]="isEntryTypeReadOnly()"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>
                    {{ item ? t(item.labelKey) : '-' }}
                  </ng-template>
                  <ng-template pTemplate="item" let-item>
                    {{ t(item.labelKey) }}
                  </ng-template>
                </p-select>
              </label>
            }
            @if (fixedEntryType() === 'closing') {
              <label class="wide">
                <span>{{ t('finance.closing.counterAccount') }}</span>
                <ain-financial-account-selector
                  [bookId]="form.bookId"
                  [ngModel]="closingCounterAccountId"
                  (ngModelChange)="closingCounterAccountId = $event ?? ''" />
              </label>
            }
            <label>
              <span>{{ t('finance.journal.fields.status') }}</span>
              <input pInputText type="text" [ngModel]="form.status" disabled />
            </label>
            <label class="wide">
              <span>{{ t('finance.journal.fields.description') }}</span>
              <input pInputText type="text" [(ngModel)]="form.description" />
            </label>
          </div>
        </section>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'items'" (click)="activeTab.set('items')">{{ t('finance.journal.tabs.items') }}</button>
            <button type="button" [class.active]="activeTab() === 'attachments'" (click)="activeTab.set('attachments')">{{ t('finance.journal.tabs.attachments') }}</button>
          </div>

          @if (activeTab() === 'items') {
            <div class="lines-header">
              <h4>{{ t('finance.journal.lines.title') }}</h4>
              <button pButton type="button" [label]="t('finance.journal.lines.add')" (click)="addLine()"></button>
            </div>
            <p-table [value]="lines()" styleClass="lines-table">
              <ng-template pTemplate="header">
                <tr>
                  <th class="col-row-index">#</th>
                  <th class="col-account">{{ t('finance.journal.lines.account') }}</th>
                  <th class="col-floating">{{ t('finance.journal.lines.floatingAccount') }}</th>
                  <th class="col-description">{{ t('finance.journal.lines.description') }}</th>
                  <th class="col-amount">{{ t('finance.journal.lines.debit') }}</th>
                  <th class="col-amount">{{ t('finance.journal.lines.credit') }}</th>
                  <th class="col-actions"></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-line let-i="rowIndex">
                <tr>
                  <td class="col-row-index">{{ i + 1 }}</td>
                  <td class="col-account">
                    <ain-financial-account-selector
                      [bookId]="form.bookId"
                      placeholderKey="finance.journal.lines.selectAccount"
                      [ngModel]="line.accountId"
                      (ngModelChange)="onLineAccountChanged(i, $event)" />
                  </td>
                  <td class="col-floating">
                    <p-select
                      class="field-select"
                      [options]="floatingOptionsForLine(line)"
                      optionLabel="label"
                      optionValue="key"
                      [placeholder]="floatingPlaceholder(line)"
                      [ngModel]="line.floatingEntityKey"
                      [disabled]="!isFloatingEnabled(line)"
                      (ngModelChange)="line.floatingEntityKey = $event ?? ''"
                      appendTo="body">
                    </p-select>
                  </td>
                  <td class="col-description"><input pInputText type="text" [(ngModel)]="line.description" /></td>
                  <td class="col-amount">
                    <p-inputNumber [(ngModel)]="line.debit" [min]="0" [maxFractionDigits]="2" [useGrouping]="true" />
                  </td>
                  <td class="col-amount">
                    <p-inputNumber [(ngModel)]="line.credit" [min]="0" [maxFractionDigits]="2" [useGrouping]="true" />
                  </td>
                  <td class="col-actions"><button pButton type="button" [label]="t('common.delete')" severity="danger" text="true" (click)="removeLine(i)"></button></td>
                </tr>
              </ng-template>
            </p-table>
            <div class="totals">
              <span>{{ t('finance.journal.totals.debit') }}: {{ totalDebit() }}</span>
              <span>{{ t('finance.journal.totals.credit') }}: {{ totalCredit() }}</span>
              <span [class.invalid]="!isBalanced()">{{ isBalanced() ? t('finance.journal.totals.balanced') : t('finance.journal.totals.unbalanced') }}</span>
            </div>
          } @else {
            <ain-attachments-panel
              [items]="attachmentItems()"
              [emptyMessage]="t('finance.journal.attachments.empty')"
              (upload)="onAttachmentUpload($event)"
              (deleteItem)="removeAttachment($event)" />
          }
        </section>
      }

      @if (errorMessage()) {
        <p class="message error">{{ errorMessage() }}</p>
      }
      @if (successMessage()) {
        <p class="message success">{{ successMessage() }}</p>
      }

      <p-dialog
        [header]="t('common.delete')"
        [visible]="deleteDialogOpen()"
        [modal]="true"
        [style]="{ width: '28rem', maxWidth: '95vw' }"
        (visibleChange)="onDeleteDialogVisibleChange($event)">
        <p>{{ t('finance.journal.messages.deleteConfirm') }}</p>
        <div class="dialog-footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeDeleteDialog()"></button>
          <button pButton type="button" severity="danger" [label]="t('common.delete')" (click)="confirmDeleteJournal()"></button>
        </div>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 10px; margin-bottom: 10px; display: grid; gap: 8px; font-size: 12px; }
    .toolbar { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
    .field-select { width: 100%; }
    .filter-book { min-width: 240px; }
    .editor-header { position: sticky; top: 8px; z-index: 5; }
    .header-top { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .form-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
    .wide { grid-column: span 4; }
    label { display: grid; gap: 4px; font-size: 12px; color: #334155; }
    input[type='text'], input[type='number'], input[type='date'] { height: 30px; border: 1px solid #d1d5db; border-radius: 8px; padding: 0 8px; width: 100%; font-size: 12px; }
    :host ::ng-deep .p-button { font-size: 12px; padding: 0.35rem 0.6rem; }
    :host ::ng-deep .p-select { min-height: 30px; font-size: 12px; }
    :host ::ng-deep .p-select .p-select-label {
      padding: 0.35rem 0.5rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    :host ::ng-deep .p-select-panel { max-width: min(42rem, 95vw); }
    :host ::ng-deep .p-select-panel .p-select-option {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    :host ::ng-deep ain-currency-selector .selector,
    :host ::ng-deep ain-financial-account-selector .selector { min-height: 30px; font-size: 12px; }
    .action-buttons { display: flex; flex-wrap: wrap; gap: 4px; }
    .tabs { display: flex; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 8px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .lines-header { display: flex; justify-content: space-between; align-items: center; }
    :host ::ng-deep .lines-table table { table-layout: fixed; width: 100%; }
    :host ::ng-deep .lines-table td,
    :host ::ng-deep .lines-table th { overflow: hidden; }
    .col-row-index { width: 42px; }
    .col-account { min-width: 200px; max-width: 280px; }
    .col-floating { min-width: 200px; max-width: 280px; }
    .col-description { min-width: 100px; max-width: 150px; }
    .col-amount { width: 200px; }
    .col-actions { width: 74px; }
    :host ::ng-deep .col-amount .p-inputnumber { width: 100%; }
    :host ::ng-deep .col-amount .p-inputnumber-input { width: 100%; text-align: end; }
    .totals { display: flex; gap: 16px; font-weight: 600; }
    .totals .invalid { color: #b91c1c; }
    .muted { color: #64748b; margin: 0; }
    .actions { display: flex; gap: 8px; }
    .message { margin: 0; font-size: 13px; }
    .error { color: #b91c1c; }
    .success { color: #166534; }
    .dialog-footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
    @media (max-width: 1100px) {
      .form-grid { grid-template-columns: 1fr 1fr; }
      .wide { grid-column: span 2; }
    }
    @media (max-width: 700px) {
      .form-grid { grid-template-columns: 1fr; }
      .wide { grid-column: span 1; }
    }
  `]
})
export class FinanceJournalEntryComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private listRoute = '/financial/management/journal-entry';
  private createRoute = '/financial/management/journal-entry/create';

  readonly fixedEntryType = signal<FixedJournalEntryType | null>(null);
  readonly pageTitleKey = signal('finance.journal.title');
  readonly pageDescriptionKey = signal('finance.journal.description');

  readonly mode = signal<'list' | 'edit'>('list');
  readonly activeTab = signal<JournalTab>('items');
  readonly editJournalId = signal<string | null>(null);
  readonly books = signal<FinancialBook[]>([]);
  readonly periods = signal<FiscalPeriodRecord[]>([]);
  readonly accounts = signal<FinancialAccountRecord[]>([]);
  readonly journals = signal<JournalEntryRecord[]>([]);
  readonly lines = signal<DraftJournalLine[]>([]);
  readonly attachments = signal<DraftAttachment[]>([]);
  readonly attachmentItems = computed<AinAttachmentItem[]>(() =>
    this.attachments().map(item => ({
      id: item.name,
      fileName: item.name,
      file: item.file
    })));
  readonly floatingCustomers = signal<AccountingCustomerRecord[]>([]);
  readonly floatingBanks = signal<BankRecord[]>([]);
  readonly floatingCashboxes = signal<CashboxRecord[]>([]);
  readonly floatingWarehouses = signal<AccountingWarehouseRecord[]>([]);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');
  readonly deleteDialogOpen = signal(false);
  readonly numberPreviewLoading = signal(false);
  readonly generateLinesLoading = signal(false);

  searchBookId = '';
  closingCounterAccountId = '';
  private pendingDeleteJournal: JournalEntryRecord | null = null;
  private isAutoNumber = false;

  readonly journalTypeOptions = [
    { value: 'manual', labelKey: 'finance.journal.types.manual' }
  ];
  readonly filteredJournals = computed(() => {
    return this.journals().filter(item => {
      if (this.searchBookId && item.bookId !== this.searchBookId) {
        return false;
      }

      const normalizedType = this.normalizeJournalEntryType(item.entryType);
      const fixed = this.fixedEntryType();
      if (fixed) {
        return normalizedType === fixed;
      }

      return normalizedType === 'manual';
    });
  });
  readonly totalDebit = computed(() => this.lines().reduce((sum, line) => sum + Number(line.debit || 0), 0));
  readonly totalCredit = computed(() => this.lines().reduce((sum, line) => sum + Number(line.credit || 0), 0));
  readonly isBalanced = computed(() => this.totalDebit() === this.totalCredit());

  form = {
    bookId: '',
    periodId: '',
    number: '',
    postingDateUtc: new Date().toISOString(),
    currencyCode: 'IRR',
    reference: '',
    type: 'manual',
    status: 'Draft',
    description: ''
  };

  ngOnInit(): void {
    this.applyRouteConfig();
    this.loadFloatingReferenceData();
    this.api.listBooks().subscribe({
      next: books => {
        this.books.set(books);
        this.form.bookId = books[0]?.id ?? '';
        this.searchBookId = '';
        if (this.isCreateModeRoute()) {
          this.startCreate();
          return;
        }

        if (this.isEditModeRoute()) {
          this.openEditFromRoute();
          return;
        }

        this.loadJournals();
      },
      error: () => this.errorMessage.set(this.t('finance.journal.messages.loadBooksFailed'))
    });
  }

  openCreateRoute(): void {
    void this.router.navigate([this.createRoute]);
  }

  openEditRoute(journal: JournalEntryRecord): void {
    void this.router.navigate([this.listRoute, journal.id, 'edit']);
  }

  startCreate(): void {
    const fixedType = this.fixedEntryType();
    this.mode.set('edit');
    this.activeTab.set('items');
    this.editJournalId.set(null);
    this.form = {
      bookId: this.books()[0]?.id ?? '',
      periodId: '',
      number: '',
      postingDateUtc: new Date().toISOString(),
      currencyCode: this.books()[0]?.currencyCode ?? 'IRR',
      reference: '',
      type: fixedType ?? 'manual',
      status: 'Draft',
      description: fixedType ? this.t(`finance.${fixedType}.defaultDescription`) : ''
    };
    this.lines.set([this.emptyLine(), this.emptyLine()]);
    this.attachments.set([]);
    this.closingCounterAccountId = '';
    this.loadEditContext();
    this.refreshDraftNumberPreview(true);
    this.errorMessage.set('');
    this.successMessage.set('');
  }

  startEdit(journal: JournalEntryRecord): void {
    const fixed = this.fixedEntryType();
    if (fixed && this.normalizeJournalEntryType(journal.entryType) !== fixed) {
      this.errorMessage.set(this.t('finance.journal.messages.loadFailed'));
      void this.router.navigate([this.listRoute]);
      return;
    }

    this.mode.set('edit');
    this.activeTab.set('items');
    this.editJournalId.set(journal.id);
    this.form.bookId = journal.bookId;
    this.form.periodId = journal.periodId;
    this.form.number = journal.number;
    this.form.postingDateUtc = journal.postingDateUtc;
    this.form.currencyCode = journal.currencyCode;
    this.form.reference = '';
    this.form.type = this.normalizeJournalEntryType(journal.entryType);
    this.form.status = journal.status;
    this.form.description = journal.description;
    this.lines.set(journal.lines.map(line => ({
      accountId: line.accountId,
      floatingEntityKey: line.floatingEntityId ?? '',
      description: line.description ?? '',
      reference: '',
      debit: line.debit,
      credit: line.credit
    })));
    if (this.lines().length === 0) {
      this.lines.set([this.emptyLine(), this.emptyLine()]);
    }
    this.attachments.set([]);
    this.loadEditContext();
    this.errorMessage.set('');
    this.successMessage.set('');
  }

  backToList(): void {
    if (this.isCreateModeRoute() || this.isEditModeRoute()) {
      void this.router.navigate([this.listRoute]);
      return;
    }

    this.mode.set('list');
    this.editJournalId.set(null);
    this.activeTab.set('items');
    this.loadJournals();
  }

  onEditBookChanged(): void {
    this.loadEditContext();
    if (!this.editJournalId()) {
      this.refreshDraftNumberPreview(this.isAutoNumber || !this.form.number.trim());
    }
  }

  onNumberChanged(value: string): void {
    this.form.number = value ?? '';
    this.isAutoNumber = false;
  }

  canGeneratePeriodLines(): boolean {
    return this.fixedEntryType() !== null && !this.editJournalId();
  }

  generateLinesLabel(): string {
    const fixed = this.fixedEntryType();
    if (fixed === 'opening') {
      return this.t('finance.opening.generateLines');
    }
    if (fixed === 'closing') {
      return this.t('finance.closing.generateLines');
    }
    return this.t('finance.periodJournal.generateLines');
  }

  generatePeriodLines(): void {
    const fixed = this.fixedEntryType();
    if (!fixed || !this.form.bookId || !this.form.periodId) {
      this.errorMessage.set(this.t('finance.journal.messages.invalidForm'));
      return;
    }

    this.errorMessage.set('');
    this.successMessage.set('');
    this.generateLinesLoading.set(true);

    const request$ = fixed === 'opening'
      ? this.api.previewOpeningJournalLines(this.form.bookId, this.form.periodId)
      : this.api.previewClosingJournalLines(
          this.form.bookId,
          this.form.periodId,
          this.closingCounterAccountId.trim() || undefined
        );

    request$.subscribe({
      next: preview => {
        this.generateLinesLoading.set(false);

        if (preview.lines.length === 0) {
          const warning = preview.warningCode?.trim();
          this.errorMessage.set(
            warning
              ? this.t(`finance.periodJournal.warnings.${warning}`)
              : this.t('finance.periodJournal.messages.emptyPreview')
          );
          return;
        }

        if (preview.suggestedCounterAccountId && !this.closingCounterAccountId) {
          this.closingCounterAccountId = preview.suggestedCounterAccountId;
        }

        this.lines.set(preview.lines.map(line => ({
          accountId: line.accountId,
          floatingEntityKey: line.floatingEntityId ?? '',
          description: line.description ?? '',
          reference: '',
          debit: line.debit,
          credit: line.credit
        })));
        this.sanitizeFloatingSelections();
        this.successMessage.set(this.t('finance.periodJournal.messages.generated'));
      },
      error: () => {
        this.generateLinesLoading.set(false);
        this.errorMessage.set(this.t('finance.periodJournal.messages.generateFailed'));
      }
    });
  }

  saveJournal(): void {
    this.errorMessage.set('');
    this.successMessage.set('');
    if (!this.form.bookId || !this.form.periodId) {
      this.errorMessage.set(this.t('finance.journal.messages.invalidForm'));
      return;
    }
    if (!this.form.currencyCode.trim()) {
      this.errorMessage.set(this.t('finance.journal.messages.invalidForm'));
      return;
    }

    const normalizedLines = this.lines()
      .map(line => ({
        accountId: line.accountId,
        debit: Number(line.debit || 0),
        credit: Number(line.credit || 0),
        description: [line.description.trim(), line.reference.trim()].filter(Boolean).join(' | '),
        floatingEntityType: this.floatingTypeByAccountId(line.accountId) === 'None'
          ? undefined
          : this.floatingTypeByAccountId(line.accountId),
        floatingEntityId: line.floatingEntityKey.trim() || undefined
      }))
      .filter(line => line.accountId && (line.debit > 0 || line.credit > 0));

    if (normalizedLines.length === 0) {
      this.errorMessage.set(this.t('finance.journal.messages.noLines'));
      return;
    }
    if (!this.isBalanced()) {
      this.errorMessage.set(this.t('finance.journal.messages.unbalanced'));
      return;
    }

    if (!this.editJournalId()) {
      const payload: CreateJournalEntryPayload = {
        bookId: this.form.bookId,
        periodId: this.form.periodId,
        number: this.form.number.trim(),
        postingDateUtc: this.form.postingDateUtc,
        currencyCode: this.form.currencyCode.trim().toUpperCase(),
        description: this.form.description.trim(),
        entryType: this.resolvedEntryTypeForSave(),
        lines: normalizedLines
      };
      this.api.createJournal(payload).subscribe({
        next: () => {
          this.successMessage.set(this.t('finance.journal.messages.created'));
          this.backToList();
        },
        error: () => this.errorMessage.set(this.t('finance.journal.messages.saveFailed'))
      });
      return;
    }

    if (!this.form.number.trim()) {
      this.errorMessage.set(this.t('finance.journal.messages.invalidForm'));
      return;
    }

    const payload: UpdateJournalEntryPayload = {
      periodId: this.form.periodId,
      number: this.form.number.trim(),
      postingDateUtc: this.form.postingDateUtc,
      currencyCode: this.form.currencyCode.trim().toUpperCase(),
      description: this.form.description.trim(),
      entryType: this.resolvedEntryTypeForSave(),
      lines: normalizedLines
    };
    this.api.updateJournal(this.editJournalId()!, payload).subscribe({
      next: () => {
        this.successMessage.set(this.t('finance.journal.messages.saved'));
        this.backToList();
      },
      error: () => this.errorMessage.set(this.t('finance.journal.messages.saveFailed'))
    });
  }

  addLine(): void {
    this.lines.update(items => [...items, this.emptyLine()]);
  }

  onLineAccountChanged(index: number, accountId: string): void {
    this.lines.update(items => {
      if (index < 0 || index >= items.length) {
        return items;
      }

      const next = [...items];
      const updatedLine = { ...next[index], accountId: accountId ?? '' };
      const floatingType = this.floatingTypeByAccountId(updatedLine.accountId);
      if (floatingType === 'None') {
        updatedLine.floatingEntityKey = '';
      } else {
        const options = this.floatingOptionsByType(floatingType);
        if (!options.some(item => item.key === updatedLine.floatingEntityKey)) {
          updatedLine.floatingEntityKey = '';
        }
      }
      next[index] = updatedLine;
      return next;
    });
  }

  removeLine(index: number): void {
    this.lines.update(items => items.filter((_, idx) => idx !== index));
    if (this.lines().length === 0) {
      this.lines.set([this.emptyLine()]);
    }
  }

  deleteJournal(journal: JournalEntryRecord): void {
    this.pendingDeleteJournal = journal;
    this.deleteDialogOpen.set(true);
  }

  onDeleteDialogVisibleChange(visible: boolean): void {
    if (!visible) this.closeDeleteDialog();
  }

  closeDeleteDialog(): void {
    this.deleteDialogOpen.set(false);
    this.pendingDeleteJournal = null;
  }

  confirmDeleteJournal(): void {
    const journal = this.pendingDeleteJournal;
    if (!journal) return;

    this.api.deleteJournal(journal.id).subscribe({
      next: () => {
        this.successMessage.set(this.t('finance.journal.messages.deleted'));
        this.closeDeleteDialog();
        this.loadJournals();
      },
      error: () => this.errorMessage.set(this.t('finance.journal.messages.deleteFailed'))
    });
  }

  submitJournal(journalId: string): void {
    this.api.submitJournal(journalId).subscribe({
      next: updated => this.patchJournal(updated),
      error: () => this.errorMessage.set(this.t('finance.journal.messages.actionFailed'))
    });
  }

  approveJournal(journalId: string): void {
    this.api.approveJournal(journalId).subscribe({
      next: updated => this.patchJournal(updated),
      error: () => this.errorMessage.set(this.t('finance.journal.messages.actionFailed'))
    });
  }

  postJournal(journalId: string): void {
    this.api.postJournal(journalId).subscribe({
      next: updated => this.patchJournal(updated),
      error: () => this.errorMessage.set(this.t('finance.journal.messages.actionFailed'))
    });
  }

  reverseJournal(journalId: string): void {
    this.api.reverseJournal(journalId).subscribe({
      next: () => this.loadJournals(),
      error: () => this.errorMessage.set(this.t('finance.journal.messages.actionFailed'))
    });
  }

  onAttachmentUpload(file: File): void {
    this.attachments.update(items => [...items, { name: file.name, file }]);
  }

  removeAttachment(name: string): void {
    this.attachments.update(items => items.filter(item => item.name !== name));
  }

  accountName(accountId: string): string {
    const account = this.accounts().find(item => item.id === accountId);
    return account ? account.name : '-';
  }

  bookLabel(bookId: string): string {
    const book = this.books().find(item => item.id === bookId);
    return book ? `${book.code} - ${book.name}` : '-';
  }

  searchBookOptions(): { id: string; label: string }[] {
    return [
      { id: '', label: this.t('finance.journal.allBooks') },
      ...this.books().map(book => ({ id: book.id, label: `${book.code} - ${book.name}` }))
    ];
  }

  periodLabel(period: FiscalPeriodRecord): string {
    return `${period.fiscalYear}/${period.periodNumber} (${period.status})`;
  }

  private patchJournal(updated: JournalEntryRecord): void {
    this.journals.update(items => items.map(item => item.id === updated.id ? updated : item));
  }

  loadJournals(): void {
    const bookId = this.searchBookId || undefined;
    this.api.listJournals(bookId, this.listEntryTypeFilter()).subscribe({
      next: rows => this.journals.set(rows),
      error: () => this.errorMessage.set(this.t('finance.journal.messages.loadFailed'))
    });
  }

  private loadEditContext(): void {
    if (!this.form.bookId) {
      this.periods.set([]);
      this.accounts.set([]);
      return;
    }
    this.api.listFiscalPeriods(this.form.bookId).subscribe({
      next: periods => {
        this.periods.set(periods);
        if (!this.form.periodId) {
          const fixed = this.fixedEntryType();
          if (fixed === 'opening') {
            const sorted = [...periods].sort((left, right) =>
              left.fiscalYear - right.fiscalYear || left.periodNumber - right.periodNumber);
            const selected = sorted[0];
            this.form.periodId = selected?.id ?? '';
            if (selected?.startDateUtc) {
              this.form.postingDateUtc = selected.startDateUtc;
            }
          } else {
            this.form.periodId = periods.find(item => item.status.toLowerCase() === 'open')?.id ?? periods[0]?.id ?? '';
          }
        }
      },
      error: () => this.errorMessage.set(this.t('finance.journal.messages.loadFailed'))
    });
    this.api.listFinancialAccounts(this.form.bookId).subscribe({
      next: rows => {
        this.accounts.set(rows);
        this.sanitizeFloatingSelections();
      },
      error: () => this.errorMessage.set(this.t('finance.journal.messages.loadFailed'))
    });
  }

  floatingOptionsForLine(line: DraftJournalLine): FloatingEntityOption[] {
    const floatingType = this.floatingTypeByAccountId(line.accountId);
    return this.floatingOptionsByType(floatingType);
  }

  isFloatingEnabled(line: DraftJournalLine): boolean {
    if (!line.accountId) {
      return false;
    }

    const floatingType = this.floatingTypeByAccountId(line.accountId);
    if (floatingType === 'None') {
      return false;
    }

    return this.floatingOptionsByType(floatingType).length > 0;
  }

  floatingPlaceholder(line: DraftJournalLine): string {
    if (!line.accountId) {
      return this.t('finance.journal.lines.selectAccount');
    }

    const floatingType = this.floatingTypeByAccountId(line.accountId);
    if (floatingType === 'None') {
      return this.t('finance.journal.lines.floatingDisabled');
    }

    if (this.floatingOptionsByType(floatingType).length === 0) {
      return this.t('finance.journal.lines.floatingNoOptions');
    }

    return this.t('finance.journal.lines.selectFloatingEntity');
  }

  private refreshDraftNumberPreview(force: boolean): void {
    if (this.editJournalId() || !this.form.bookId) {
      return;
    }
    if (!force && this.form.number.trim()) {
      return;
    }

    this.numberPreviewLoading.set(true);
    this.api.previewNextJournalNumber(this.form.bookId).subscribe({
      next: preview => {
        if (preview) {
          this.form.number = preview;
          this.isAutoNumber = true;
        }
        this.numberPreviewLoading.set(false);
      },
      error: () => {
        this.numberPreviewLoading.set(false);
      }
    });
  }

  private emptyLine(): DraftJournalLine {
    return {
      accountId: '',
      floatingEntityKey: '',
      description: '',
      reference: '',
      debit: 0,
      credit: 0
    };
  }

  isEntryTypeReadOnly(): boolean {
    return this.fixedEntryType() !== null || this.form.type === 'mechanized';
  }

  private applyRouteConfig(): void {
    const data = this.resolveMergedRouteData();
    const baseRoute = typeof data['baseRoute'] === 'string' ? data['baseRoute'] : null;
    if (baseRoute) {
      this.listRoute = baseRoute;
      this.createRoute = `${baseRoute}/create`;
    }

    if (typeof data['titleKey'] === 'string') {
      this.pageTitleKey.set(data['titleKey']);
    }
    if (typeof data['descriptionKey'] === 'string') {
      this.pageDescriptionKey.set(data['descriptionKey']);
    }

    const fixedEntryType = data['fixedEntryType'];
    if (fixedEntryType === 'opening' || fixedEntryType === 'closing') {
      this.fixedEntryType.set(fixedEntryType);
    }
  }

  private resolveMergedRouteData(): Record<string, unknown> {
    let merged: Record<string, unknown> = {};
    let snapshot = this.route.snapshot;
    while (snapshot) {
      merged = { ...merged, ...snapshot.data };
      snapshot = snapshot.firstChild!;
    }
    return merged;
  }

  private listEntryTypeFilter(): string | undefined {
    const fixed = this.fixedEntryType();
    if (fixed === 'opening') {
      return 'Opening';
    }
    if (fixed === 'closing') {
      return 'Closing';
    }
    return 'Manual';
  }

  private resolvedEntryTypeForSave(): string {
    const fixed = this.fixedEntryType();
    if (fixed === 'opening') {
      return 'Opening';
    }
    if (fixed === 'closing') {
      return 'Closing';
    }
    return this.form.type;
  }

  private normalizeJournalEntryType(value?: string): string {
    const normalized = (value ?? 'Manual').trim().toLowerCase();
    if (normalized === 'opening' || normalized === '1') {
      return 'opening';
    }
    if (normalized === 'closing' || normalized === '2') {
      return 'closing';
    }
    if (normalized === 'mechanized' || normalized === '4') {
      return 'mechanized';
    }
    return 'manual';
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private isCreateModeRoute(): boolean {
    return this.route.snapshot.data['createMode'] === true;
  }

  private isEditModeRoute(): boolean {
    return this.route.snapshot.data['editMode'] === true;
  }

  private openEditFromRoute(): void {
    const journalId = this.route.snapshot.paramMap.get('journalId');
    if (!journalId) {
      void this.router.navigate([this.listRoute]);
      return;
    }

    this.api.listJournals(undefined, this.listEntryTypeFilter()).subscribe({
      next: rows => {
        this.journals.set(rows);
        const journal = rows.find(item => item.id === journalId);
        if (!journal) {
          this.errorMessage.set(this.t('finance.journal.messages.loadFailed'));
          void this.router.navigate([this.listRoute]);
          return;
        }

        this.startEdit(journal);
      },
      error: () => {
        this.errorMessage.set(this.t('finance.journal.messages.loadFailed'));
        void this.router.navigate([this.listRoute]);
      }
    });
  }

  private loadFloatingReferenceData(): void {
    this.api.listAccountingCustomers().subscribe({
      next: rows => this.floatingCustomers.set(rows),
      error: () => this.floatingCustomers.set([])
    });

    this.api.listAccountingBanks().subscribe({
      next: rows => this.floatingBanks.set(rows),
      error: () => this.floatingBanks.set([])
    });

    this.api.listAccountingCashboxes().subscribe({
      next: rows => this.floatingCashboxes.set(rows),
      error: () => this.floatingCashboxes.set([])
    });

    this.api.listInventoryWarehouses().subscribe({
      next: rows => this.floatingWarehouses.set(rows.filter(item => item.isActive)),
      error: () => this.floatingWarehouses.set([])
    });
  }

  private floatingTypeByAccountId(accountId: string): FloatingEntityType {
    if (!accountId) {
      return 'None';
    }

    const account = this.accounts().find(item => item.id === accountId);
    return this.normalizeFloatingEntityType(account?.floatingEntityType);
  }

  private normalizeFloatingEntityType(value?: string): FloatingEntityType {
    switch (value) {
      case 'Person':
      case 'OrganizationUnit':
      case 'Bank':
      case 'CashFund':
      case 'Warehouse':
        return value;
      default:
        return 'None';
    }
  }

  private floatingOptionsByType(type: FloatingEntityType): FloatingEntityOption[] {
    switch (type) {
      case 'Person':
        return this.floatingCustomers().map(item => ({
          key: item.key,
          label: item.code ? `${item.displayName} (${item.code})` : item.displayName
        }));
      case 'Bank':
        return this.floatingBanks().map(item => ({
          key: item.id,
          label: item.number ? `${item.number} - ${item.bankName}` : item.bankName
        }));
      case 'CashFund':
        return this.floatingCashboxes().map(item => ({
          key: item.id,
          label: item.number ? `${item.number} - ${item.name}` : item.name
        }));
      case 'Warehouse':
        return this.floatingWarehouses().map(item => ({
          key: item.id,
          label: item.code ? `${item.code} - ${item.name}` : item.name
        }));
      default:
        return [];
    }
  }

  private sanitizeFloatingSelections(): void {
    this.lines.update(items => items.map(line => {
      const floatingType = this.floatingTypeByAccountId(line.accountId);
      if (floatingType === 'None') {
        return { ...line, floatingEntityKey: '' };
      }

      const options = this.floatingOptionsByType(floatingType);
      if (!options.some(item => item.key === line.floatingEntityKey)) {
        return { ...line, floatingEntityKey: '' };
      }

      return line;
    }));
  }
}
